// Fake-tractor GPS simulator. Drives either freely (heading + speed) or along a path with a
// controllable lateral offset (the demo driver: nudge the tractor off the lane to watch the
// deviation react). Produces GnssFix and the NMEA the real receiver would emit, so nothing
// downstream knows it isn't a real u-blox.

import {
  FixQuality,
  projectorForLon,
  encodeGGA,
  encodeRMC,
  perpRight,
  normalize,
  sub,
  add,
  scale,
  len,
  type GnssFix,
  type LonLat,
  type Projector,
  type Vec2,
} from '@fla/core';
import { mulberry32, gaussian } from './rng';

export interface TractorSimConfig {
  /** Initial position [lon, lat]. */
  start: LonLat;
  /** Sim clock start, epoch ms (sim time advances by the dt you step). */
  startTimestamp: number;
  /** Initial true heading, degrees (0 = north). Default 0. */
  headingDeg?: number;
  /** Ground speed, m/s. Default 2.5 (~9 km/h, typical field speed). */
  speedMps?: number;
  /** PRNG seed for reproducible noise. Default 1. */
  seed?: number;
  /** Lateral position noise stddev, meters. Default 0.015 (RTK-fixed grade). */
  noiseM?: number;
  /** Reported HDOP. Default 0.6. */
  hdop?: number;
  /** Reported satellites in use. Default 18. */
  satellites?: number;
  /** Probability per step of being in RTK float instead of fixed. Default 0.03. */
  floatProbability?: number;
}

function bearingFromVec(u: Vec2): number {
  // u = [East, North]. bearing 0 = North, 90 = East.
  let deg = (Math.atan2(u[0], u[1]) * 180) / Math.PI;
  if (deg < 0) deg += 360;
  return deg;
}

function dirFromBearing(deg: number): Vec2 {
  const r = (deg * Math.PI) / 180;
  return [Math.sin(r), Math.cos(r)];
}

/** Sample a polyline (meters) at arc-length `s`, returning the point and unit tangent. */
function sampleAlong(path: Vec2[], s: number): { point: Vec2; tangent: Vec2 } {
  if (path.length === 1) return { point: path[0]!, tangent: [0, 1] };
  let acc = 0;
  for (let i = 0; i + 1 < path.length; i++) {
    const a = path[i]!;
    const b = path[i + 1]!;
    const seg = sub(b, a);
    const l = len(seg);
    if (l === 0) continue;
    if (acc + l >= s) {
      const t = (s - acc) / l;
      return { point: add(a, scale(seg, t)), tangent: normalize(seg) };
    }
    acc += l;
  }
  // past the end: clamp to the last point with the last tangent
  const a = path[path.length - 2]!;
  const b = path[path.length - 1]!;
  return { point: b, tangent: normalize(sub(b, a)) };
}

function pathLength(path: Vec2[]): number {
  let total = 0;
  for (let i = 0; i + 1 < path.length; i++) total += len(sub(path[i + 1]!, path[i]!));
  return total;
}

export class TractorSimulator {
  private readonly proj: Projector;
  private pos: Vec2; // meters
  private bearingDeg: number;
  private speed: number;
  private readonly rand: () => number;
  private readonly noiseM: number;
  private readonly hdop: number;
  private readonly satellites: number;
  private readonly floatProbability: number;
  private t: number;
  private quality: FixQuality = FixQuality.RTKFixed;

  // Path-follow mode (null = free drive).
  private path: Vec2[] | null = null;
  private pathLen = 0;
  private s = 0; // along-track distance, meters
  private offsetM = 0; // lateral offset from path (right +)
  private finished = false;

  constructor(cfg: TractorSimConfig) {
    this.proj = projectorForLon(cfg.start[0]);
    this.pos = this.proj.toMeters(cfg.start);
    this.bearingDeg = cfg.headingDeg ?? 0;
    this.speed = cfg.speedMps ?? 2.5;
    this.rand = mulberry32(cfg.seed ?? 1);
    this.noiseM = cfg.noiseM ?? 0.015;
    this.hdop = cfg.hdop ?? 0.6;
    this.satellites = cfg.satellites ?? 18;
    this.floatProbability = cfg.floatProbability ?? 0.03;
    this.t = cfg.startTimestamp;
  }

  /** Switch to path-follow mode along the given lon/lat polyline. */
  followPath(path: LonLat[], opts: { offsetM?: number; fromStart?: boolean } = {}): void {
    this.path = path.map((p) => this.proj.toMeters(p));
    this.pathLen = pathLength(this.path);
    this.offsetM = opts.offsetM ?? 0;
    this.finished = false;
    if (opts.fromStart !== false) {
      this.s = 0;
      const { point } = sampleAlong(this.path, 0);
      this.pos = point;
    }
  }

  /** Lateral offset from the path center, meters. Positive = right of travel. */
  setOffset(m: number): void {
    this.offsetM = m;
  }
  /** Nudge the current lateral offset by `delta` meters. */
  nudge(delta: number): void {
    this.offsetM += delta;
  }
  setSpeed(mps: number): void {
    this.speed = Math.max(0, mps);
  }
  setHeading(deg: number): void {
    this.bearingDeg = ((deg % 360) + 360) % 360;
  }

  /** True once a followed path has been driven to its end. */
  isFinished(): boolean {
    return this.finished;
  }

  /** Advance the simulation by `dtSeconds` and return the resulting fix. */
  step(dtSeconds: number): GnssFix {
    const dist = this.speed * dtSeconds;
    if (this.path) {
      this.s += dist;
      if (this.s >= this.pathLen) {
        this.s = this.pathLen;
        this.finished = true;
      }
      const { point, tangent } = sampleAlong(this.path, this.s);
      this.bearingDeg = bearingFromVec(tangent);
      const right = perpRight(tangent);
      const noise = gaussian(this.rand) * this.noiseM;
      this.pos = add(point, scale(right, this.offsetM + noise));
    } else {
      const u = dirFromBearing(this.bearingDeg);
      const noise = gaussian(this.rand) * this.noiseM;
      const right = perpRight(u);
      this.pos = add(add(this.pos, scale(u, dist)), scale(right, noise));
    }
    this.t += Math.round(dtSeconds * 1000);
    this.quality = this.rand() < this.floatProbability ? FixQuality.RTKFloat : FixQuality.RTKFixed;
    return this.current();
  }

  /** The current fix without advancing time. */
  current(): GnssFix {
    const [lon, lat] = this.proj.toLonLat(this.pos);
    return {
      timestamp: this.t,
      lat,
      lon,
      quality: this.quality,
      satellites: this.satellites,
      hdop: this.hdop,
      altitude: 300,
      speed: this.speed,
      heading: this.bearingDeg,
    };
  }

  /** The current fix as NMEA sentences [GGA, RMC] — exactly what the receiver would emit. */
  toNmea(): [string, string] {
    const fix = this.current();
    return [encodeGGA(fix), encodeRMC(fix)];
  }
}
